<template>
  <el-dialog
    :title="isEdit ? '编辑任务' : '新增任务'"
    :visible.sync="dialogVisible"
    width="900px"
    :close-on-click-modal="false"
    append-to-body
    top="5vh"
    class="task-edit-dialog"
    @open="handleOpen"
  >
    <!-- 新增：所属任务组展示 -->
    <div
      v-if="groupName"
      class="task-group-summary"
    >
      <span class="label">所属任务组：</span>
      <span class="value">{{ groupName }}</span>
    </div>

    <el-steps
      :active="activeStep"
      finish-status="success"
      simple
      style="margin-bottom: 15px"
    >
      <el-step
        title="任务配置"
        icon="el-icon-edit"
      />
      <el-step
        title="生成预览"
        icon="el-icon-view"
      />
    </el-steps>

    <div
      v-loading="previewLoading"
      class="step-content"
    >
      <el-form
        v-show="activeStep === 0"
        ref="form"
        :model="formData"
        :rules="rules"
        label-width="110px"
      >
        <el-form-item
          label="任务名称"
          prop="taskName"
        >
          <el-input
            v-model="formData.taskName"
            placeholder="留空将自动生成"
          />
        </el-form-item>

        <!-- 任务期次：新增时按周期选或填；编辑时展示，仅专项任务组可改名称 -->
        <el-form-item prop="periodName">
          <template slot="label">
            <span class="form-label-with-tip">
              <span>任务期次</span>
              <el-tooltip
                content="任务期次即该任务的周期名称，用于实际执行中显示的任务名称。"
                placement="top"
              >
                <i class="el-icon-question period-name-tip-icon" />
              </el-tooltip>
            </span>
          </template>
          <template v-if="!isEdit">
            <!-- 日常：期次即自然日，与列表筛选及后端 period_name（yyyy-MM-dd）一致 -->
            <el-date-picker
              v-if="cycleType === 'day'"
              v-model="formData.periodName"
              type="date"
              placeholder="请选择任务期次"
              value-format="yyyy-MM-dd"
              :picker-options="dayPeriodPickerOptions"
              style="width: 100%"
              @change="handleDayPeriodChange"
            />
            <!-- 专项：直接输入 -->
            <el-input
              v-else-if="cycleType === 'special'"
              v-model="formData.periodName"
              placeholder="如：春节专项检查"
            />
            <!-- 其他周期：下拉选择 -->
            <template v-else>
              <el-select
                v-model="formData.periodName"
                style="width: 100%"
                placeholder="请选择期次名称"
                @change="handlePeriodNameChange"
              >
                <el-option
                  v-for="item in periodNameOptions"
                  :key="item.label"
                  :label="item.label"
                  :value="item.label"
                />
              </el-select>
              <div
                v-if="showSplitMonthlyHint"
                class="field-tip"
              >
                本任务组已按月分解，请选择具体月份作为任务期次（与创建任务组时的月度期次一致）。
              </div>
            </template>
          </template>
          <template v-else>
            <el-input
              v-if="cycleType === 'special'"
              v-model="formData.periodName"
              placeholder="如：春节专项检查"
            />
            <el-input
              v-else
              v-model="formData.periodName"
              disabled
            />
            <div
              v-if="cycleType !== 'special'"
              class="field-tip"
            >
              非专项任务组下任务期次名称不可修改
            </div>
          </template>
        </el-form-item>

        <el-row :gutter="10">
          <el-col :span="24">
            <el-form-item
              label="消防系统"
              prop="systemId"
            >
              <el-select
                v-model="formData.systemId"
                style="width: 100%"
                :disabled="isEdit"
                placeholder="请选择"
                @change="handleSystemChange"
              >
                <el-option
                  v-for="item in systemList"
                  :key="item.uuid"
                  :label="item.systemName"
                  :value="item.uuid"
                />
              </el-select>
              <div
                v-if="isEdit"
                class="field-tip"
              >
                编辑时不允许更换系统
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="24">
            <el-form-item label="设施类型">
              <el-select
                v-model="formData.deviceTypeIds"
                multiple
                collapse-tags
                style="width: 100%"
                placeholder="全部类型"
              >
                <el-option
                  v-for="item in filteredTypeList"
                  :key="item.uuid"
                  :label="item.typeName"
                  :value="item.uuid"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 子区域范围（新增/编辑均显示，限定在任务组大区节点下） -->
        <el-form-item label="子区域范围">
          <!-- 走公共组件：它固定 expandTrigger=hover 并带整行点击选中，裸 el-cascader 默认是 click 展开且多选不可点行选中 -->
          <AreaCascader
            v-model="formData.areaIds"
            :options="zoneSubAreas"
            multiple
            :check-strictly="false"
            collapse-tags
            placeholder="默认全大区设备"
            style="width: 100%"
            clearable
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              label="计划执行日期"
              prop="executeDate"
            >
              <el-date-picker
                v-model="formData.executeDate"
                type="date"
                placeholder="请选择"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="截止日期">
              <el-date-picker
                v-model="formData.deadlineDate"
                type="date"
                placeholder="选填"
                :disabled="periodDeadlineLocked"
                style="width: 100%"
              />
              <!-- 同一期次的截止日期需跨消防系统一致，这里把「回填锁定」与「编辑会同步整期次」提前讲清楚 -->
              <div
                v-if="periodDeadlineLocked"
                class="field-tip"
              >
                本期次已有截止日期，需与本期次其他消防系统保持一致
              </div>
              <div
                v-else-if="isEdit"
                class="field-tip"
              >
                修改后将同步本任务期次下所有消防系统的任务
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <step3-preview
        v-show="activeStep === 1"
        :preview-data="previewData"
        @warning-change="onPreviewWarningChange"
      />
    </div>

    <div slot="footer">
      <el-button
        v-if="activeStep === 0"
        @click="dialogVisible = false"
      >
        取消
      </el-button>
      <el-button
        v-if="activeStep === 1"
        @click="prevStep"
      >
        上一步
      </el-button>
      <el-button
        v-if="activeStep === 0"
        type="primary"
        @click="nextStep"
      >
        下一步
      </el-button>
      <el-button
        v-if="activeStep === 1"
        type="primary"
        :loading="submitting"
        :disabled="!canSubmit"
        @click="handleSubmit"
      >
        确认保存
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
// 服务
import { taskService } from './task.service';
// 工具函数
import {
  buildMonthlySplitPeriodConfigs,
  isTaskYearMonthSelectable,
  isTaskYearQuarterSelectable,
  isTaskYearFirstHalfSelectable
} from './utils/dateCalc';
import { collectAllAreaIds } from './utils/areaHandler';
import { isCycleWithoutTaskYear, isYearSplitMonthlyCycle } from '@/features/gnmk/constants/taskCycleEnums';
import Step3Preview from './task-group-dialog/Step3Preview.vue';
import AreaCascader from '@/features/gnmk/components/AreaCascader.vue';

export default {
  name: 'TaskDialog',
  // 同时注册 kebab-case，避免部分构建链下 PascalCase 标签未解析为局部组件
  components: {
    Step3Preview,
    'step3-preview': Step3Preview,
    AreaCascader
  },
  props: {
    visible: { type: Boolean, default: false },
    taskData: { type: Object, default: null },
    // 当前所属任务组名称，用于在弹窗中展示分组信息
    groupName: { type: String, default: '' },
    groupId: { type: String, default: null },
    cycleType: { type: String, default: null },
    taskType: { type: String, default: null },
    // 任务组年份，用于生成周期名称选项
    taskYear: { type: String, default: null },
    // 任务组大区ID，用于过滤子区域
    zoneAreaId: { type: String, default: null },
    /** 任务组是否按月分解（Y/N），与创建任务组 isSplitMonthly 一致；年度/年度日查时影响期次下拉 */
    isSplitMonthly: { type: [String, Boolean], default: 'N' },
    systemList: { type: Array, default: () => [] },
    typeList: { type: Array, default: () => [] },
    areaTree: { type: Array, default: () => [] }
  },
  data () {
    return {
      /** 0 表单 / 1 预览 */
      activeStep: 0,
      previewLoading: false,
      previewData: null,
      hasTypeWarning: false,
      formData: {
        taskName: null,
        systemId: null,
        deviceTypeIds: [],
        periodName: null,
        areaIds: [],
        executeDate: null,
        deadlineDate: null
      },
      rules: {
        systemId: [{ required: true, message: '请选择消防系统', trigger: 'change' }],
        executeDate: [{ required: true, message: '请选择计划执行日期', trigger: 'change' }],
        periodName: [{ required: true, message: '请选择或填写任务期次', trigger: 'blur' }]
      },
      /** 新增态：同期次已有截止日期时回填并锁定，避免用户填出同组同期次不一致的值 */
      periodDeadlineLocked: false,
      submitting: false
    };
  },
  computed: {
    dialogVisible: {
      get () { return this.visible; },
      set (val) { this.$emit('update:visible', val); }
    },
    isEdit () { return !!this.taskData; },

    /** 设施类型按当前选中系统过滤 */
    filteredTypeList () {
      if (!this.formData.systemId) return [];
      return this.typeList.filter(t => t.systemId === this.formData.systemId);
    },

    /**
     * 子区域选项：限定在任务组大区节点下，避免跨大区选择
     */
    zoneSubAreas () {
      if (!this.zoneAreaId || !this.areaTree.length) return this.areaTree;
      const findNode = (nodes, id) => {
        for (const n of nodes) {
          if (n.uuid === id) return n;
          if (n.children) { const r = findNode(n.children, id); if (r) return r; }
        }
        return null;
      };
      const zone = findNode(this.areaTree, this.zoneAreaId);
      return zone ? [zone] : this.areaTree;
    },

    /** 年度或年度日查且任务组开启了按月分解 */
    groupSplitMonthlyActive () {
      const t = this.cycleType;
      if (t !== 'year' && t !== 'yearly_daily') return false;
      const v = this.isSplitMonthly;
      return v === true || v === 'Y' || v === 'y';
    },

    showSplitMonthlyHint () {
      return !this.isEdit && this.groupSplitMonthlyActive;
    },

    /**
     * 任务组年份：路由或接口可能为 null，不能用 Number(taskYear)|| 省略（Number(null) 为 0）
     */
    resolvedTaskYear () {
      const raw = this.taskYear;
      if (raw === null || raw === undefined || raw === '') {
        return new Date().getFullYear();
      }
      const n = typeof raw === 'string' ? parseInt(raw, 10) : Number(raw);
      return Number.isFinite(n) && n > 0 ? n : new Date().getFullYear();
    },

    /**
     * 新建/编辑任务期次：任务年份为今年时去掉已过去的月/季度/半年（与列表筛选「整年」下拉不同）。
     */
    periodNameOptions () {
      const year = this.resolvedTaskYear;
      const type = this.cycleType;
      const now = new Date();
      if (type === 'month') {
        return Array.from({ length: 12 }, (_, i) => {
          const m = i + 1;
          return { label: `${year}年${m}月`, date: new Date(year, i, 1) };
        }).filter((_, i) => isTaskYearMonthSelectable(year, i, now));
      }
      if (type === 'quarter') {
        return [1, 2, 3, 4].map(q => ({
          label: `${year}年第${q}季度`,
          date: new Date(year, (q - 1) * 3, 1)
        })).filter((_, idx) => isTaskYearQuarterSelectable(year, idx + 1, now));
      }
      if (type === 'half_year') {
        const halves = [
          { label: `${year}年上半年`, date: new Date(year, 0, 1) },
          { label: `${year}年下半年`, date: new Date(year, 6, 1) }
        ];
        return halves.filter((_, idx) => (idx === 0 ? isTaskYearFirstHalfSelectable(year, now) : true));
      }
      if (type === 'year' || type === 'yearly_daily') {
        if (this.groupSplitMonthlyActive) {
          const list = buildMonthlySplitPeriodConfigs(year, new Date());
          if (list && list.length) {
            return list.map(p => ({
              label: p.periodName,
              date: p.executeDate,
              deadlineDate: p.deadlineDate
            }));
          }
        }
        return [{ label: `${year}年`, date: new Date(year, 0, 1), deadlineDate: null }];
      }
      return [];
    },

    /** 预览中是否存在「有设备但无检查项」的子任务 */
    hasEmptyCheckContent () {
      const tasks = this.previewData && this.previewData.subTasks;
      if (!Array.isArray(tasks)) return false;
      return tasks.some(t => t.deviceCount > 0 && !t.checkContentCount);
    },
    canSubmit () {
      if (!this.previewData) return false;
      if (!this.previewData.totalDevices) return false;
      if (!this.previewData.totalSubTasks) return false;
      if (this.hasEmptyCheckContent) return false;
      if (this.hasTypeWarning) return false;
      return true;
    },

    /** 日常任务期次：不可选择今天之前的日期 */
    dayPeriodPickerOptions () {
      return {
        disabledDate (time) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          return time.getTime() < today.getTime();
        }
      };
    }
  },
  methods: {
    handleOpen () {
      this.activeStep = 0;
      this.previewData = null;
      this.hasTypeWarning = false;
      this.periodDeadlineLocked = false;

      this.$nextTick(() => this.$refs.form && this.$refs.form.clearValidate());

      if (this.isEdit) {
        // 编辑回填：deviceTypeIds / areaIds 均从逗号分隔字符串解析
        const deviceTypeIds = this.taskData.deviceTypes
          ? this.taskData.deviceTypes.split(',').filter(Boolean)
          : [];
        const areaIds = this.taskData.areaIds
          ? this.taskData.areaIds.split(',').filter(Boolean)
          : [];
        this.formData = {
          taskName: this.taskData.taskName,
          systemId: this.taskData.systemId,
          deviceTypeIds,
          periodName: this.taskData.periodName,
          areaIds,
          executeDate: this.taskData.executeDate ? new Date(this.taskData.executeDate) : null,
          deadlineDate: this.taskData.deadlineDate ? new Date(this.taskData.deadlineDate) : null
        };
      } else {
        // 新增重置
        const todayStr = this.formatDateStr(new Date());
        this.formData = {
          taskName: null,
          systemId: null,
          deviceTypeIds: [],
          periodName: this.cycleType === 'day' ? todayStr : null,
          areaIds: [],
          executeDate: this.cycleType === 'day' || this.cycleType === 'special' ? new Date() : null,
          deadlineDate: null
        };
      }
    },

    handleSystemChange () {
      this.formData.deviceTypeIds = [];
    },

    /** 日常任务：期次日期变更时同步计划执行日期 */
    handleDayPeriodChange (dateStr) {
      if (!dateStr) return;
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        this.formData.executeDate = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      }
      this.refreshPeriodDeadline();
    },

    /** 选择周期名称时自动填充计划执行日期；按月分解时同步默认截止日期（自然月末，与任务组周期一致） */
    handlePeriodNameChange (label) {
      const option = this.periodNameOptions.find(o => o.label === label);
      if (option && option.date) {
        this.formData.executeDate = option.date;
      }
      if (option && option.deadlineDate) {
        this.formData.deadlineDate = option.deadlineDate;
      }
      // 放在默认值之后：期次已有截止日期时以同期次已有值为准
      this.refreshPeriodDeadline();
    },

    formatDateStr (date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },

    /**
     * 与任务组创建 preview 一致：单期次 + 单系统配置，区域 ID 经 areaHandler 展开
     */
    buildPreviewPayload () {
      const expandedAreaIds = this.formData.areaIds && this.formData.areaIds.length > 0
        ? collectAllAreaIds(this.formData.areaIds, this.areaTree)
        : [];

      let taskYearVal = null;
      if (!isCycleWithoutTaskYear(this.cycleType)) {
        taskYearVal = String(this.resolvedTaskYear);
      }

      const showSplit = isYearSplitMonthlyCycle(this.cycleType);
      const on = this.isSplitMonthly === true || this.isSplitMonthly === 'Y';
      const splitFlag = showSplit && on ? 'Y' : 'N';

      const deviceTypeIds = this.formData.deviceTypeIds && this.formData.deviceTypeIds.length
        ? [...this.formData.deviceTypeIds]
        : [];

      return {
        groupName: this.groupName || '预览',
        taskType: this.taskType,
        cycleType: this.cycleType,
        taskYear: taskYearVal,
        zoneAreaId: this.zoneAreaId,
        isSplitMonthly: splitFlag,
        assignType: 'none',
        assignUserIds: null,
        assignTeamId: null,
        periodConfigs: [{
          periodName: this.formData.periodName,
          executeDate: this.formData.executeDate,
          deadlineDate: this.formData.deadlineDate,
          systemConfigs: [{
            systemId: this.formData.systemId,
            deviceTypeIds,
            areaIds: expandedAreaIds
          }]
        }]
      };
    },

    /**
     * 解析预览接口 body：兼容直连 VO 与 `{ data: VO }` 包装；保证 subTasks 为数组，避免 el-table 收到 null 渲染报错
     */
    normalizePreviewResponse (axiosBody) {
      if (axiosBody == null || typeof axiosBody !== 'object') return null;
      let vo = axiosBody;
      const inner = axiosBody.data;
      if (inner != null && typeof inner === 'object' &&
          (Array.isArray(inner.subTasks) || inner.totalSubTasks != null || inner.totalDevices != null)) {
        vo = inner;
      }
      return {
        ...vo,
        subTasks: Array.isArray(vo.subTasks) ? vo.subTasks : []
      };
    },

    fetchPreview () {
      this.previewLoading = true;
      const payload = this.buildPreviewPayload();
      taskService.previewTaskGroup(payload).then(res => {
        this.previewData = this.normalizePreviewResponse(res.data);
      }).catch(() => {
        this.previewData = null;
      }).finally(() => {
        this.previewLoading = false;
      });
    },

    onPreviewWarningChange ({ hasDeviceTypeWarning, hasCheckTypeWarning }) {
      this.hasTypeWarning = hasDeviceTypeWarning || hasCheckTypeWarning;
    },

    /**
     * 拉取同期次（同任务组 + 同任务期次）的全部子任务，一次请求同时支撑两件事：
     * 1) 重复校验：后端 saveTask 的幂等规则是「相同期次 + 相同消防系统」不可重复新增；
     * 2) 截止日期回填：提醒弹窗按期次聚合且只展示最早的截止日期，
     *    所以必须看整个期次已有的值，不能只按表单里当前选中的消防系统查。
     */
    async fetchPeriodTasks () {
      if (!this.groupId || !this.formData.periodName) return [];
      try {
        const res = await taskService.listTasks({
          groupId: this.groupId,
          periodName: this.formData.periodName
        });
        const body = res && res.data;
        if (Array.isArray(body)) return body;
        return (body && (body.list || body.rows || body.data)) || [];
      } catch {
        // 查询失败不阻断新增流程，退化为不校验、不回填，由后端兜底
        return [];
      }
    },

    /** 期次截止日期取最早值，与后端 TaskService.resolvePeriodDeadline 保持同一口径 */
    pickPeriodDeadline (tasks) {
      const times = tasks
        .map(t => (t && t.deadlineDate ? new Date(t.deadlineDate).getTime() : NaN))
        .filter(time => !Number.isNaN(time));
      return times.length ? new Date(Math.min(...times)) : null;
    },

    /**
     * 按同期次已有值刷新截止日期（仅新增态）：
     * 期次内已有截止日期时回填并锁定输入，期次内还没有任务时保留用户填写的值。
     * 返回同期次任务列表，供调用方复用（不再重复请求）。
     */
    refreshPeriodDeadline () {
      if (this.isEdit) return Promise.resolve([]);
      const staleLockedValue = this.periodDeadlineLocked ? this.formData.deadlineDate : null;
      return this.fetchPeriodTasks().then(tasks => {
        this.periodDeadlineLocked = false;
        const deadline = this.pickPeriodDeadline(tasks);
        if (deadline) {
          this.formData.deadlineDate = deadline;
          this.periodDeadlineLocked = true;
        } else if (staleLockedValue) {
          // 上一个期次锁定回填的值不能带到新期次
          this.formData.deadlineDate = null;
        }
        return tasks;
      });
    },

    nextStep () {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        const goPreview = () => {
          this.fetchPreview();
          this.activeStep = 1;
        };
        if (this.isEdit) {
          goPreview();
          return;
        }
        // 专项任务组的期次是自由输入框，没有 change 事件，进入预览前必须再兜一次
        this.refreshPeriodDeadline().then(tasks => {
          if (tasks.some(t => t && t.systemId === this.formData.systemId)) {
            this.$message.warning('该任务期次与消防系统的任务已存在，请勿重复创建');
            return;
          }
          goPreview();
        });
      });
    },

    prevStep () {
      if (this.activeStep > 0) this.activeStep--;
    },

    handleSubmit () {
      if (this.activeStep !== 1) return;
      this.$refs.form.validate(valid => {
        if (!valid) return;
        this.submitting = true;
        const expandedAreaIds = this.formData.areaIds && this.formData.areaIds.length > 0
          ? collectAllAreaIds(this.formData.areaIds, this.areaTree)
          : [];
        const data = {
          uuid: this.isEdit ? this.taskData.uuid : '',
          groupId: this.groupId,
          taskName: this.formData.taskName || '',
          systemId: this.formData.systemId,
          deviceTypeIds: this.formData.deviceTypeIds,
          periodName: this.formData.periodName,
          areaIds: expandedAreaIds,
          executeDate: this.formData.executeDate,
          deadlineDate: this.formData.deadlineDate
        };
        const request = this.isEdit
          ? taskService.updateTask(data)
          : taskService.saveTask(data);

        request.then(() => {
          this.$message.success(this.isEdit ? '编辑成功' : '新增成功');
          this.dialogVisible = false;
          this.$emit('refresh');
        }).finally(() => {
          this.submitting = false;
        });
      });
    }
  }
};
</script>

<style scoped lang="scss">
.task-edit-dialog {
  .step-content {
    min-height: 420px;
  }
}

.form-label-with-tip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.period-name-tip-icon {
  color: #909399;
  font-size: 14px;
  cursor: help;
  font-weight: normal;
}

.field-tip {
  font-size: 12px;
  color: #e6a23c;
  margin-top: 4px;
}

.section-label {
  font-size: 13px;
  color: #909399;
}
.task-group-summary {
  margin-bottom: 10px;
  padding: 6px 10px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;

  .label {
    color: #909399;
    margin-right: 6px;
  }

  .value {
    color: #303133;
    font-weight: 500;
  }
}

::v-deep .el-form-item {
  margin-bottom: 18px;
}

::v-deep .el-form-item__error {
  position: relative;
  top: 0;
  padding-top: 2px;
}
</style>
